class RentalCheckout {
    constructor() {
      this.formDataCache = {};
      this.cartId = new URLSearchParams(window.location.search).get('cart_id');
      this.customerToken = localStorage.getItem('customerAccessToken');
      this.baseApiUrl = 'https://wonderent.wonderchef.com';
      this.initializeElements();
      this.bindEvents();
      this.loadCheckoutData();
      this.setupZipCodeValidation();
      
      // Add placeholders configuration
      this.placeholders = {
          name: 'Name not provided',
          address: 'Address not provided',
          city: 'City not provided',
          state: 'State not provided',
          postalCode: 'Postal code not provided',
          phone: 'Phone not provided',
          // Add more placeholders as needed
      };

      // Add required fields configuration
      this.requiredFields = {
          first_name: 'First Name',
          last_name: 'Last Name',
          address: 'Address',
          city: 'City',
          state: 'State',
          postal_code: 'Postal Code',
          phone: 'Phone Number'
      };
    }
  
    initializeElements() {
      this.shippingAddressesContainer = document.getElementById('shipping-addresses-container');
      this.shippingForm = document.getElementById('shipping-address-form');
      this.cartItemsContainer = document.getElementById('cart-items');
      this.orderTotals = document.getElementById('order-totals');
      this.razorpayButton = document.getElementById('razorpay-button');
      this.newAddressButton = document.getElementById('use-different-address');
      this.sameAsShipping = document.getElementById('same_as_shipping');
  
      // Add CSS for checkbox alignment
      const styleElement = document.createElement('style');
      styleElement.textContent = `
        .checkbox-group {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 10px 0;
        }
  
        .checkbox-group input[type="checkbox"] {
          margin: 0;
          position: relative;
          top: 0;  /* Removes any default positioning */
        }
  
        .checkbox-group label {
          display: inline-flex;
          align-items: center;
          margin: 0;
          line-height: 1;
        }

        .highlight-required {
            border-color: #dc3545 !important;
            background-color: #fff8f8;
        }

        .field-warning {
            color: #dc3545;
            font-size: 0.875rem;
            margin-top: 0.25rem;
        }

        .address-warning {
            background-color: #fff8f8;
            border: 1px solid #dc3545;
            border-radius: 4px;
            padding: 1rem;
            margin-bottom: 1rem;
        }

        .address-warning ul {
            margin: 0.5rem 0;
            padding-left: 1.5rem;
        }

        .address-warning p {
            margin: 0;
        }
      `;
      document.head.appendChild(styleElement);
    }
  
    bindEvents() {
      if (this.razorpayButton) {
        this.razorpayButton.addEventListener('click', (e) => this.handlePayment(e));
      }
      
      if (this.newAddressButton) {
        this.newAddressButton.addEventListener('click', () => this.showAddressForm());
      }
    }
  
    async loadCheckoutData() {
      try {
        const headers = {
          'Content-Type': 'application/json'
        };

        // Add customer token to headers if available
        if (this.customerToken) {
          headers['X-Shopify-Customer-Access-Token'] = this.customerToken;
        }

        const response = await fetch(`${this.baseApiUrl}/api/checkout/`, {
          headers: headers,
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        this.renderCheckoutData(data);
      } catch (error) {
        console.error('Error loading checkout data:', error);
      }
    }
  
    renderCheckoutData(data) {
      if (!data) return;
      
      // Changed from data.addresses to data.saved_addresses
      this.renderAddresses(data.saved_addresses);
      this.renderCartItems(data.cart_items);
      this.renderTotals(data);
      
      // Setup Razorpay options
      this.razorpayOptions = {
        key: data.razorpay_key_id,
        amount: data.total_amount * 100,
        currency: "INR",
        name: "WonderChef",
        description: "Rental Order Payment",
        order_id: data.razorpay_order_id,
        handler: (response) => this.verifyPayment(response),
        prefill: data.user_details || {},
        theme: {
          color: "#127cc8"
        }
      };
    }
  
    renderAddresses(addresses) {
      // console.log('Rendering addresses:', addresses);
  
      if (!addresses || addresses.length === 0) {
        // console.log('No saved addresses found, showing address form');
        this.showAddressForm();
        return;
      }
  
      const addressesHtml = addresses.map(address => {
        const fullName = `${address.first_name || this.getPlaceholder('name')} ${address.last_name || ''}`.trim();
        const addressLine1 = address.address1 || this.getPlaceholder('address');
        const cityStateZip = [
            address.city || this.getPlaceholder('city'),
            address.state || this.getPlaceholder('state'),
            address.postal_code || this.getPlaceholder('postalCode')
        ].filter(Boolean).join(', ');

        return `
            <div class="address-card"
                 data-address-id="${address.id}"
                 data-first-name="${address.first_name || ''}"
                 data-last-name="${address.last_name || ''}"
                 data-address="${address.address1 || ''}"
                 data-apartment="${address.address2 || ''}"
                 data-city="${address.city || ''}"
                 data-state="${address.state || ''}"
                 data-postal-code="${address.postal_code || ''}"
                 data-phone="${address.phone || ''}"
                 onclick="rentalCheckout.selectAddress(this, 'shipping')">
                <div class="address-content">
                    <h3>${fullName}</h3>
                    <p>${addressLine1}</p>
                    ${address.address2 ? `<p>${address.address2}</p>` : ''}
                    <p>${cityStateZip}</p>
                    ${address.phone ? `<p>${address.phone}</p>` : ''}
                </div>
            </div>
        `;
      }).join('');
  
      // console.log('Generated address HTML:', addressesHtml);
      
      if (this.shippingAddressesContainer) {
        this.shippingAddressesContainer.innerHTML = addressesHtml;
        this.shippingForm.style.display = 'none';
      } else {
        console.error('Shipping addresses container not found');
      }
    }
  
    renderCartItems(items) {
      if (!items || !this.cartItemsContainer) return;
  
      const itemsHtml = items.map(item => `
        <div class="cart-item">
          <div class="item-image">
            <img src="${item.image_url}" alt="${item.title}">
            <div class="quantity-badge">1</div>
          </div>
          <div class="item-details">
            <h3>${item.title}</h3>
            <div class="price">₹${item.rent}</div>
          </div>
        </div>
      `).join('');
  
      this.cartItemsContainer.innerHTML = itemsHtml;
    }
  
    renderTotals(data) {
       if (!this.orderTotals) return;

       // Use the pre-calculated values from the API
       const totalRent = parseFloat(data.total_rent || 0);
       const securityDeposit = parseFloat(data.total_deposit || 0);
       const subtotal = parseFloat(data.subtotal || 0);

       // Extract rental tenure information from cart items
       const rentalTenures = data.cart_items?.map(item => item.rental_period).filter(Boolean) || [];
       const uniqueTenures = [...new Set(rentalTenures)];
       const tenureDisplay = uniqueTenures.length > 0
         ? uniqueTenures.map(tenure => `${tenure} Months`).join(', ')
         : 'N/A';

       this.orderTotals.innerHTML = `
         <div class="subtotal">
           <span>Total Rent</span>
           <span>₹${totalRent.toFixed(2)}</span>
         </div>
         <div class="rental-tenure">
           <span>Rental Tenure</span>
           <span>${tenureDisplay}</span>
         </div>
         <div class="security-deposit">
           <span>Security Deposit</span>
           <span>₹${securityDeposit.toFixed(2)}</span>
         </div>
         <div class="shipping">
           <span>Delivery</span>
           <span><del>₹99</del>  ₹0</span>
         </div>
         <div class="total">
           <span>Total Amount</span>
           <span>₹${subtotal.toFixed(2)}</span>
         </div>
       `;
     }
  
    getAddressFields(type) {
      const fields = {
        first_name: document.getElementById(`${type}_first_name`),
        last_name: document.getElementById(`${type}_last_name`),
        address: document.getElementById(`${type}_address`),
        apartment: document.getElementById(`${type}_apartment`),
        city: document.getElementById(`${type}_city`),
        state: document.getElementById(`${type}_state`),
        pincode: document.getElementById(`${type}_pincode`),
        phone: document.getElementById(`${type}_phone`)
      };
  
      // console.log(`Getting ${type} address fields:`, fields); // Debug log
      return fields;
    }
  
    handleSameAsShipping() {
      if (!this.sameAsShipping) return;
      
      const billingContent = document.getElementById('billing-address-content');
      if (billingContent) {
        billingContent.style.display = this.sameAsShipping.checked ? 'none' : 'block';
      }
      
      if (this.sameAsShipping.checked) {
        this.syncAddresses();
      }
    }
  
    syncAddresses() {
      if (!this.sameAsShipping?.checked) return;
      
      const shippingFields = this.getAddressFields('shipping');
      const billingFields = this.getAddressFields('billing');
      
      Object.keys(shippingFields).forEach(key => {
        if (billingFields[key] && shippingFields[key]) {
          billingFields[key].value = shippingFields[key].value;
        }
      });
    }
  
    selectAddress(addressCard, type) {
      // console.log('Selecting address:', addressCard.dataset); // Debug log
  
      // Remove selected class from all cards
      const cards = document.querySelectorAll('.address-card');
      cards.forEach(card => card.classList.remove('selected'));
      
      // Add selected class to clicked card
      addressCard.classList.add('selected');
      
      const fields = this.getAddressFields(type);
      const mappings = {
        first_name: 'firstName',
        last_name: 'lastName',
        address: 'address',
        apartment: 'apartment',
        city: 'city',
        state: 'state',
        pincode: 'postalCode',
        phone: 'phone'
      };

      // Clear previous warnings
      document.querySelectorAll('.field-warning').forEach(warning => warning.remove());
      document.querySelectorAll('.highlight-required').forEach(field => 
          field.classList.remove('highlight-required'));

      let missingRequiredFields = [];

      // Populate fields and check for missing required data
      Object.entries(mappings).forEach(([fieldKey, dataKey]) => {
        if (fields[fieldKey]) {
          const value = addressCard.dataset[dataKey] || '';
          fields[fieldKey].value = value;

          // Check if this is a required field and it's empty
          if (this.requiredFields[fieldKey] && !value) {
            missingRequiredFields.push(this.requiredFields[fieldKey]);
            fields[fieldKey].classList.add('highlight-required');
            
            // Add warning message below the field
            const warning = document.createElement('div');
            warning.className = 'field-warning';
            warning.textContent = `Please provide ${this.requiredFields[fieldKey]}`;
            fields[fieldKey].parentNode.appendChild(warning);
          }
        }
      });

      // Show warning message if there are missing required fields
      if (missingRequiredFields.length > 0) {
        const warningContainer = document.createElement('div');
        warningContainer.className = 'address-warning';
        warningContainer.innerHTML = `
            <p>This address is missing the following required information:</p>
            <ul>
                ${missingRequiredFields.map(field => `<li>${field}</li>`).join('')}
            </ul>
            <p>Please fill in the highlighted fields before proceeding.</p>
        `;
        
        // Insert warning at the top of the form
        if (this.shippingForm) {
          this.shippingForm.insertBefore(warningContainer, this.shippingForm.firstChild);
        }
      }

      // Hide form if an address is selected and all required fields are present
      if (this.shippingForm && missingRequiredFields.length === 0) {
        this.shippingForm.style.display = 'none';
      } else {
        this.shippingForm.style.display = 'block';
      }
      
      if (this.sameAsShipping?.checked) {
        this.syncAddresses();
      }
    }
  
    validateForm() {
      const requiredFields = ['first_name', 'last_name', 'address', 'city', 'state', 'pincode', 'phone'];
      const missingFields = requiredFields.filter(field => {
        const element = document.getElementById(`shipping_${field}`);
        return !element || !element.value.trim();
      });
  
      if (missingFields.length > 0) {
        alert('Please fill in all required fields before proceeding to payment.');
        return false;
      }
  
      // Validate email
      const customerEmail = document.querySelector('[data-customer-email]')?.getAttribute('data-customer-email');
      if (!customerEmail) {
        alert('Please log in to continue with checkout.');
        return false;
      }
  
      return true;
    }
  
    cacheFormData() {
      const customerEmail = document.querySelector('[data-customer-email]')?.getAttribute('data-customer-email') || '';
      const shippingFields = this.getAddressFields('shipping');
      
      this.formDataCache = {
   
        email: customerEmail,
        shipping_address: {
          email: customerEmail,
          first_name: shippingFields.first_name?.value,
          last_name: shippingFields.last_name?.value,
          address: shippingFields.address?.value,
          apartment: shippingFields.apartment?.value,
          city: shippingFields.city?.value,
          state: shippingFields.state?.value,
          pincode: shippingFields.pincode?.value,
          phone: shippingFields.phone?.value
        },
        same_as_shipping: true
      };
      
      // console.log('Cached form data:', this.formDataCache);
    }
  
    async verifyPayment(response) {
      try {
        // Get customer email
        const customerEmail = document.querySelector('[data-customer-email]')?.getAttribute('data-customer-email') || '';
  
        const formData = {
          ...this.formDataCache,
          email: customerEmail,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_order_id: response.razorpay_order_id,
          razorpay_signature: response.razorpay_signature
        };
  
        // console.log('Sending payment verification data:', formData);
  
        const verifyResponse = await fetch(`${this.baseApiUrl}/api/process-checkout/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCookie('csrftoken')
          },
          credentials: 'include',
          body: JSON.stringify(formData)
        });
  
        if (!verifyResponse.ok) {
          const errorData = await verifyResponse.json();
          throw new Error(errorData.error || 'Payment verification failed');
        }
  
        const data = await verifyResponse.json();
        if (data.redirect_url) {
          window.location.href = data.redirect_url;
        } else if (data.error) {
          alert('Error: ' + data.error);
        }
      } catch (error) {
      //  console.error('Payment verification error:', error);
        alert('An unexpected error occurred. Please try again.');
      }
    }
  
    getCookie(name) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
    }
  
    async handlePayment(e) {
      try {
        e.preventDefault();
        
        if (!this.validateForm()) return;
        
        this.cacheFormData();
        
        // Debug log before opening Razorpay
        // console.log('Razorpay options:', this.razorpayOptions);
        
        const rzp = new Razorpay(this.razorpayOptions);
        rzp.open();
      } catch (error) {
      //  console.error('Payment error:', error);
      }
    }
  
    async saveAddress(addressData) {
      try {
        const response = await fetch(`${this.baseApiUrl}/api/rentals/save-address/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCookie('csrftoken')
          },
          body: JSON.stringify(addressData)
        });
        return await response.json();
      } catch (error) {
      //  console.error('Error saving address:', error);
        throw error;
      }
    }
  
    async updateAddress(addressId, addressData) {
      try {
        const response = await fetch(`${this.baseApiUrl}/api/rentals/update-address/${addressId}/`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCookie('csrftoken')
          },
          body: JSON.stringify(addressData)
        });
        return await response.json();
      } catch (error) {
      //  console.error('Error updating address:', error);
        throw error;
      }
    }
  
    async deleteAddress(addressId) {
      try {
        const response = await fetch(`${this.baseApiUrl}/api/rentals/delete-address/${addressId}/`, {
          method: 'DELETE',
          headers: {
            'X-CSRFToken': this.getCookie('csrftoken')
          }
        });
        return await response.json();
      } catch (error) {
      //  console.error('Error deleting address:', error);
        throw error;
      }
    }
  
    async processCheckout() {
      try {
        const headers = {
          'Content-Type': 'application/json'
        };
        
        // Add customer token if available
        if (window.customer) {
          headers['X-Shopify-Customer-Access-Token'] = localStorage.getItem('customerAccessToken');
        }
        
        const response = await fetch(`${API_BASE_URL}/api/process-checkout/`, {
          method: 'POST',
          headers: headers,
          body: JSON.stringify(this.formDataCache)
        });
        
        const data = await response.json();
        if (data.error) {
          throw new Error(data.error);
        }
        return data;
      } catch (error) {
      //  console.error('Error processing checkout:', error);
        throw error;
      }
    }
  
    showAddressForm() {
      if (this.shippingForm) {
        this.shippingForm.style.display = 'block';
      }
      if (this.shippingAddressesContainer) {
        this.shippingAddressesContainer.style.display = 'none';
      }
    }
  
    hideAddressForm() {
      if (this.shippingForm) {
        this.shippingForm.style.display = 'none';
      }
      if (this.shippingAddressesContainer) {
        this.shippingAddressesContainer.style.display = 'grid';
      }
    }
  
    async checkLoginStatus() {
      const customerEmail = document.querySelector('[data-customer-email]')?.getAttribute('data-customer-email');
      if (!customerEmail) {
        window.location.href = '/account/login?checkout_redirect=' + encodeURIComponent(window.location.pathname);
        return false;
      }
      return true;
    }
  
    setupZipCodeValidation() {
      const pincodeInput = document.getElementById('shipping_pincode');
      if (!pincodeInput) return;
  
      let timeoutId;
      pincodeInput.addEventListener('input', (e) => {
        // Clear any existing timeout
        clearTimeout(timeoutId);
        
        const pincode = e.target.value.trim();
        // Only validate if we have exactly 6 digits
        if (pincode.length === 6) {
          // Add small delay to prevent too many API calls
          timeoutId = setTimeout(() => this.validateZipCode(pincode), 500);
        }
      });
    }
  
    async validateZipCode(pincode) {
      try {
        const response = await fetch(`${this.baseApiUrl}/api/validate-zip-code/?pin_code=${pincode}`);
        const data = await response.json();
        
        const pincodeInput = document.getElementById('shipping_pincode');
        const errorDiv = pincodeInput.parentElement.querySelector('.pincode-error') || 
                       document.createElement('div');
        
        errorDiv.className = 'pincode-error';
        
        if (data.valid) {
          // Only auto-fill state if available
          const stateInput = document.getElementById('shipping_state');
          if (stateInput) stateInput.value = data.state;
          
          pincodeInput.style.borderColor = '#28a745';
          errorDiv.style.color = '#28a745';
          errorDiv.textContent = '✓ Delivery available';
        } else {
          pincodeInput.style.borderColor = '#dc3545';
          errorDiv.style.color = '#dc3545';
          errorDiv.textContent = data.message || 'Delivery not available';
        }
        
        if (!pincodeInput.parentElement.querySelector('.pincode-error')) {
          pincodeInput.parentElement.appendChild(errorDiv);
        }
        
      } catch (error) {
      //  console.error('Error validating ZIP code:', error);
      }
    }

    // Add this utility method to get placeholder text
    getPlaceholder(field) {
        return this.placeholders[field] || 'Not provided';
    }
  }
  
  // Initialize the checkout when DOM is loaded
  let rentalCheckout;
  document.addEventListener('DOMContentLoaded', () => {
    rentalCheckout = new RentalCheckout();
  });